/**
 * Merchant memory. A `MerchantRule` is created
 * only with the user's consent when a proposal is approved, counts how many
 * times it was confirmed, and may pre-fill automatically after two identical
 * confirmations. Rules are working state: they pre-fill proposals and never
 * book anything.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { IsoDateTime, MerchantRule, Proposal, TransactionKind, Uuid, VatChoice } from '../types';
import { merchantDisplayName, merchantKeyOf } from './normalize';

/** What the user confirmed for a merchant; the part of a proposal a rule remembers. */
export interface ConfirmedChoice {
  kind: TransactionKind;
  categoryId: string;
  vat: VatChoice;
  paymentSourceId: Uuid | null;
}

export type RuleDecision =
  /** No rule yet: create one when the user wants it remembered. */
  | { action: 'create' }
  /** Same choice as the rule: bump `timesUsed`. */
  | { action: 'same' }
  /** The user chose differently: ask "Uppdatera regeln?" before replacing it. */
  | { action: 'differs'; changed: ('kind' | 'categoryId' | 'vat' | 'paymentSourceId')[] };

/** Confirmations a second category needs before the merchant counts as mixed. */
export const AMBIGUOUS_AFTER = 2;

/** Confirmations per category, falling back to the pre-`categoryCounts` shape. */
export function categoryCountsOf(rule: MerchantRule): Record<string, number> {
  return rule.categoryCounts ?? { [rule.categoryId]: rule.timesUsed };
}

/**
 * Does this merchant get booked on more than one category in earnest?
 *
 * A single stray purchase does not unsettle a rule — Clas Ohlson is still a
 * kontorsmaterial shop after one printer. Two confirmations of a second
 * category is a pattern, and from then on memory has no category worth
 * asserting: the receipt in hand knows better than the six before it.
 */
export function isAmbiguousCategory(rule: MerchantRule): boolean {
  const counts = Object.values(categoryCountsOf(rule)).filter((n) => n >= AMBIGUOUS_AFTER);
  return counts.length >= 2;
}

/** The category confirmed most often, for a merchant that still has one. */
export function dominantCategory(rule: MerchantRule): string {
  const entries = Object.entries(categoryCountsOf(rule));
  return entries.sort((a, b) => b[1] - a[1])[0]?.[0] ?? rule.categoryId;
}

function bumpCategory(rule: MerchantRule, categoryId: string): Record<string, number> {
  const counts = { ...categoryCountsOf(rule) };
  counts[categoryId] = (counts[categoryId] ?? 0) + 1;
  return counts;
}

function sameVat(a: VatChoice, b: VatChoice): boolean {
  return a.rate === b.rate && a.mode === b.mode;
}

/** Pure: how a confirmation relates to the existing rule for that merchant. */
export function ruleDecision(existing: MerchantRule | undefined, confirmed: ConfirmedChoice): RuleDecision {
  if (!existing) return { action: 'create' };
  const changed: ('kind' | 'categoryId' | 'vat' | 'paymentSourceId')[] = [];
  if (existing.kind !== confirmed.kind) changed.push('kind');
  // Once the merchant is mixed there is no remembered category to differ from,
  // so "Uppdatera regeln?" stops asking on every other purchase.
  if (!isAmbiguousCategory(existing) && existing.categoryId !== confirmed.categoryId) changed.push('categoryId');
  if (!sameVat(existing.vat, confirmed.vat)) changed.push('vat');
  if ((existing.paymentSourceId ?? null) !== (confirmed.paymentSourceId ?? null)) changed.push('paymentSourceId');
  if (changed.length) return { action: 'differs', changed };
  return { action: 'same' };
}

/** "Uppdatera regeln?" sheet copy for a differing confirmation. */
export function updateRuleQuestion(rule: MerchantRule): { title: string; body: string; confirm: string; cancel: string } {
  return {
    title: 'Uppdatera regeln?',
    body: `Du har tidigare bokfört ${rule.displayName} på ett annat sätt (${rule.timesUsed} ${rule.timesUsed === 1 ? 'gång' : 'gånger'}). Ska vi komma ihåg det nya valet i stället?`,
    confirm: 'Ja, uppdatera',
    cancel: 'Nej, bara den här gången',
  };
}

/**
 * "Förifyllt från minnet – du har bokfört Clas Ohlson som Kontorsmaterial 6
 * gånger". Counted per category, so the number never borrows confirmations
 * from a category the rule no longer holds.
 */
export function memoryReasonText(rule: MerchantRule, categoryLabel: string): string {
  const n = categoryCountsOf(rule)[rule.categoryId] ?? rule.timesUsed;
  return `Förifyllt från minnet – du har bokfört ${rule.displayName} som ${categoryLabel} ${n} ${n === 1 ? 'gång' : 'gånger'}`;
}

/** What memory contributes for a merchant it has no single category for. */
export function mixedMemoryReasonText(rule: MerchantRule): string {
  return `Betalsättet är förifyllt från minnet – du bokför ${rule.displayName} på olika kategorier, så kvittot får bestämma`;
}

/**
 * Pure: apply a rule to a proposal.
 *
 * Memory answers what the document cannot — category, kind and which account
 * paid — while the amount, date and counterparty stay as read. VAT is the one
 * field memory used to take by force: a printed momsrad is a fact about this
 * purchase, so it wins, and the rule only fills in when the receipt shows
 * none. That is exactly the gap `no_vat_breakdown` exists for.
 *
 * Certainty is claimed only when memory and the receipt agree about the
 * category. A general retailer sells tea cups and printers; six confirmations
 * of "Kontorsmaterial" say nothing about the printer, so the disagreement is
 * carried out to triage rather than smoothed over here.
 */
export function applyMerchantRule(proposal: Proposal, rule: MerchantRule): Proposal {
  const ambiguous = isAmbiguousCategory(rule);
  const disagrees = !ambiguous && categoryDisagreesWith(proposal, rule);
  const next: Proposal = {
    ...proposal,
    kind: rule.kind,
    // A mixed merchant contributes no category: the receipt decides, and the
    // dominant one is a fallback only when the reading has none at all.
    categoryId: ambiguous ? (proposal.categoryId ?? dominantCategory(rule)) : rule.categoryId,
    vat: proposal.vat ?? { ...rule.vat },
    paymentSourceId: rule.paymentSourceId ?? proposal.paymentSourceId,
    /*
     * `reason` decides whether triage applies its confidence gates. Claiming
     * "memory" for a category memory did not supply would wave the AI's own
     * uncertainty through, so a mixed merchant stays a suggestion — memory
     * only quietly fills the payment account behind it.
     */
    reason: ambiguous ? proposal.reason : 'memory',
    confidence: ambiguous || disagrees ? proposal.confidence : 1,
  };
  if (!ambiguous && proposal.question && proposal.question.field !== 'persons') delete next.question;
  return next;
}

/**
 * Did the document suggest a different category than the one remembered?
 *
 * Must be asked *before* `applyMerchantRule`, which overwrites the answer.
 * A proposal with no category of its own does not disagree — it has no opinion.
 */
export function categoryDisagreesWith(proposal: Pick<Proposal, 'categoryId'>, rule: MerchantRule): boolean {
  return proposal.categoryId !== null && proposal.categoryId !== rule.categoryId;
}

export async function findMerchantRule(companyId: Uuid, merchantKey: string, db: BookkeepingDB = defaultDb): Promise<MerchantRule | undefined> {
  if (!merchantKey) return undefined;
  return db.merchantRules.where('[companyId+merchantKey]').equals([companyId, merchantKey]).first();
}

export async function findMerchantRuleByName(companyId: Uuid, name: string, db: BookkeepingDB = defaultDb): Promise<MerchantRule | undefined> {
  return findMerchantRule(companyId, merchantKeyOf(name), db);
}

export async function listMerchantRules(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<MerchantRule[]> {
  const rules = await db.merchantRules.where('companyId').equals(companyId).toArray();
  return rules.sort((a, b) => b.timesUsed - a.timesUsed || a.displayName.localeCompare(b.displayName, 'sv'));
}

export interface RememberInput {
  companyId: Uuid;
  /** The merchant as printed on the receipt / bank row. */
  merchantName: string;
  confirmed: ConfirmedChoice;
  createdFrom: MerchantRule['createdFrom'];
  /** Replace a differing rule (the user answered "Ja, uppdatera"). Default: leave it untouched. */
  replaceIfDifferent?: boolean;
  now?: IsoDateTime;
}

export interface RememberResult {
  rule: MerchantRule | undefined;
  decision: RuleDecision;
}

/**
 * Upsert on approval. Creates a rule, bumps `timesUsed` on an
 * identical confirmation, and replaces a differing rule only when asked to
 * (`replaceIfDifferent`), resetting its counter since the choice changed.
 */
export async function rememberMerchant(input: RememberInput, db: BookkeepingDB = defaultDb): Promise<RememberResult> {
  const merchantKey = merchantKeyOf(input.merchantName);
  if (!merchantKey) return { rule: undefined, decision: { action: 'create' } };
  const now = input.now ?? new Date().toISOString();
  return db.transaction('rw', db.merchantRules, async () => {
    const existing = await findMerchantRule(input.companyId, merchantKey, db);
    const decision = ruleDecision(existing, input.confirmed);
    switch (decision.action) {
      case 'create': {
        const rule: MerchantRule = {
          id: crypto.randomUUID(),
          companyId: input.companyId,
          merchantKey,
          displayName: merchantDisplayName(input.merchantName) || merchantKey,
          kind: input.confirmed.kind,
          categoryId: input.confirmed.categoryId,
          vat: { rate: input.confirmed.vat.rate, mode: input.confirmed.vat.mode },
          paymentSourceId: input.confirmed.paymentSourceId,
          timesUsed: 1,
          categoryCounts: { [input.confirmed.categoryId]: 1 },
          lastUsedAt: now,
          createdFrom: input.createdFrom,
          createdAt: now,
        };
        await db.merchantRules.add(rule);
        return { rule, decision };
      }
      case 'same': {
        const rule: MerchantRule = {
          ...existing!,
          timesUsed: existing!.timesUsed + 1,
          categoryCounts: bumpCategory(existing!, input.confirmed.categoryId),
          lastUsedAt: now,
        };
        await db.merchantRules.put(rule);
        return { rule, decision };
      }
      case 'differs': {
        /*
         * "Nej, bara den här gången" still counts. The user did book this
         * merchant on another category, and that is the only evidence the shop
         * sells more than one kind of thing — discard it and a merchant can
         * never be recognised as mixed, which is precisely the case where the
         * remembered category keeps being wrong.
         */
        const counts = bumpCategory(existing!, input.confirmed.categoryId);
        const kept: MerchantRule = { ...existing!, timesUsed: existing!.timesUsed + 1, categoryCounts: counts, lastUsedAt: now };
        if (!input.replaceIfDifferent) {
          await db.merchantRules.put(kept);
          return { rule: kept, decision };
        }
        const rule: MerchantRule = {
          ...kept,
          kind: input.confirmed.kind,
          categoryId: input.confirmed.categoryId,
          vat: { rate: input.confirmed.vat.rate, mode: input.confirmed.vat.mode },
          paymentSourceId: input.confirmed.paymentSourceId,
          createdFrom: input.createdFrom,
        };
        await db.merchantRules.put(rule);
        return { rule, decision };
      }
      default: {
        const exhaustive: never = decision;
        throw new Error(`Unknown decision ${String(exhaustive)}`);
      }
    }
  });
}

/** "Förifyll … automatiskt": the rule pre-fills without waiting for the AI. It still never books. */
/** Forget a merchant ("Glöm Clas Ohlson"). Booked verifikationer are untouched. */
export async function forgetMerchant(companyId: Uuid, merchantKey: string, db: BookkeepingDB = defaultDb): Promise<boolean> {
  const rule = await findMerchantRule(companyId, merchantKey, db);
  if (!rule) return false;
  await db.merchantRules.delete(rule.id);
  return true;
}
